import { createClient } from "@supabase/supabase-js";
import { supabase } from "../lib/supabaseClient";
import { apiError, throwIfError } from "../lib/apiError";
import {
  clearProfileCache,
  fetchCurrentProfile,
  mapProfileToUser,
  persistAccessToken,
} from "../lib/sessionUser";
import { CLINIC_MODE_SOLO_DOCTOR } from "../utils/clinicMode";
import { recordAudit } from "./auditService";

function authPayload(session, profile) {
  const user = mapProfileToUser(profile);
  return {
    accessToken: session.access_token,
    expiresAtUtc: session.expires_at
      ? new Date(session.expires_at * 1000).toISOString()
      : null,
    user,
  };
}

const passwordCheckMemory = new Map();

/** Re-check the signed-in user's password without touching the current session. */
export async function confirmCurrentPassword(password) {
  const { data: userData, error: userError } = await supabase.auth.getUser();
  const email = userData?.user?.email;
  if (userError || !email) {
    throw apiError("Sesioni nuk është i vlefshëm. Hyni përsëri.", 401);
  }
  const checker = createClient(
    process.env.REACT_APP_SUPABASE_URL || "",
    process.env.REACT_APP_SUPABASE_PUBLISHABLE_KEY || "",
    {
      auth: {
        persistSession: false,
        autoRefreshToken: false,
        detectSessionInUrl: false,
        storageKey: "clinicops-password-check",
        storage: {
          getItem: (key) => passwordCheckMemory.get(key) ?? null,
          setItem: (key, value) => {
            passwordCheckMemory.set(key, value);
          },
          removeItem: (key) => {
            passwordCheckMemory.delete(key);
          },
        },
      },
    }
  );
  const { error } = await checker.auth.signInWithPassword({
    email,
    password: password || "",
  });
  passwordCheckMemory.clear();
  if (error) throw apiError("Fjalëkalimi nuk është i saktë.", 401);
}

export async function login(email, password) {
  const { data, error } = await supabase.auth.signInWithPassword({
    email: String(email || "").trim(),
    password: password || "",
  });
  if (error) throwIfError(error, "Email ose fjalëkalim i pasaktë.");
  if (!data?.session) throw apiError("Email ose fjalëkalim i pasaktë.", 401);

  clearProfileCache();
  const profile = await fetchCurrentProfile(true);
  if (!profile?.role || !profile.is_active) {
    await supabase.auth.signOut();
    clearProfileCache();
    throw apiError(
      "Aplikimi juaj është ende në pritje të aprovimit. Do të mund të hyni pasi të aprovohet.",
      403
    );
  }

  persistAccessToken(data.session);
  const payload = authPayload(data.session, profile);
  await recordAudit({
    action: "Login",
    entityName: "User",
    entityId: profile.id,
    entityDisplayName: profile.display_name || profile.email,
    description: "Login",
    clinicId: profile.clinic_id,
  });
  return payload;
}

export async function applyForClinic(clinicName, email, password, clinicMode) {
  const mode =
    clinicMode === CLINIC_MODE_SOLO_DOCTOR || clinicMode === 0 || clinicMode === "0"
      ? "SoloDoctor"
      : "FullTeam";
  const { data, error } = await supabase.rpc("submit_clinic_application", {
    p_clinic_name: String(clinicName || "").trim(),
    p_email: String(email || "").trim(),
    p_password: password || "",
    p_clinic_mode: mode,
  });
  throwIfError(error, "Aplikimi dështoi. Provoni përsëri.");
  return { id: data };
}

export async function logout() {
  await recordAudit({
    action: "Logout",
    entityName: "User",
    description: "Logout",
  });
  clearProfileCache();
  await supabase.auth.signOut();
}
